'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isTokenizedValueMask } from '@sola/shared-utils/credit-cards.ts';
import type { InputFieldProps } from '../input-field/input-field.tsx';
import { InputField } from '../input-field/input-field.tsx';
import { removeNonDigits, resetAchRoutingAndAccount, shouldRemoveSpecialChars, validationKeys } from './utils.ts';
type ACHRoutingFieldProps = Omit<InputFieldProps, 'maskOptions' | 'placeholder' | 'prefix' | 'type' | 'minLength' | 'maxLength' | 'pattern' | 'aria-label' | 'aria-labelledby'> & {
  name: string;
  // Enables validation to prevent matching routing and account numbers
  accountNumberFieldName?: string;
};
export const ACHRoutingNumber: React.FC<ACHRoutingFieldProps> = t0 => {
  const $ = _c(46);
  let accountNumberFieldName;
  let fieldProps;
  if ($[0] !== t0) {
    ({
      accountNumberFieldName,
      ...fieldProps
    } = t0);
    $[0] = t0;
    $[1] = accountNumberFieldName;
    $[2] = fieldProps;
  } else {
    accountNumberFieldName = $[1];
    fieldProps = $[2];
  }
  const {
    name
  } = fieldProps;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["checkout", "payment-methods"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const formContext = useFormContext();
  const {
    setValue,
    getValues,
    trigger,
    formState
  } = formContext;
  const t2 = accountNumberFieldName || "";
  const t3 = !accountNumberFieldName;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      name: t2,
      disabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const accountNumber = useWatch(t4);
  let t5;
  if ($[7] !== formContext) {
    t5 = () => resetAchRoutingAndAccount(formContext);
    $[7] = formContext;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const clearValues = t5;
  let t6;
  if ($[9] !== accountNumber || $[10] !== formState.errors || $[11] !== getValues || $[12] !== name || $[13] !== trigger) {
    t6 = () => {
      if (!accountNumber || formState.errors[name]?.type !== validationKeys.differentNumbers) {
        return;
      }
      const routingNumber = getValues(name);
      if (routingNumber !== accountNumber) {
        trigger(name);
      }
    };
    $[9] = accountNumber;
    $[10] = formState.errors;
    $[11] = getValues;
    $[12] = name;
    $[13] = trigger;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== accountNumber || $[16] !== formState.errors) {
    t7 = [accountNumber, formState.errors];
    $[15] = accountNumber;
    $[16] = formState.errors;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== t) {
    t8 = t("payment-methods:ach.fields.routing");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== clearValues) {
    t9 = e => {
      if (isTokenizedValueMask(e.target.value)) {
        clearValues();
      }
    };
    $[20] = clearValues;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  let t11;
  if ($[22] !== getValues || $[23] !== name || $[24] !== setValue) {
    t10 = () => {
      setValue(name, getValues(name).trim(), {
        shouldValidate: true
      });
    };
    t11 = () => {
      const newValue = getValues(name);
      if (shouldRemoveSpecialChars(newValue)) {
        setValue(name, removeNonDigits(newValue));
      }
    };
    $[22] = getValues;
    $[23] = name;
    $[24] = setValue;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = value => {
      if (isTokenizedValueMask(value)) {
        return true;
      }
      const trimValue = value?.trim();
      const startValue = Number(trimValue.slice(0, 2));
      return startValue < 1 || startValue > 32 || startValue >= 13 && startValue <= 20 ? t("checkout:form.ach.routingInvalid") : true;
    };
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== accountNumberFieldName || $[30] !== formContext || $[31] !== t) {
    t13 = value_0 => {
      if (isTokenizedValueMask(value_0)) {
        return true;
      }
      return !accountNumberFieldName || formContext.getValues()[accountNumberFieldName] !== value_0 || t("checkout:form.ach.accountRoutingMatch");
    };
    $[29] = accountNumberFieldName;
    $[30] = formContext;
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t) {
    t14 = value_1 => {
      if (isTokenizedValueMask(value_1)) {
        return true;
      }
      const trimValue_0 = value_1?.trim();
      let sum = 0;
      for (let i = 0; i < trimValue_0.length; i = i + 3, i) {
        sum = sum + (Number.parseInt(trimValue_0.charAt(i), 10) * 3 + Number.parseInt(trimValue_0.charAt(i + 1), 10) * 7 + Number.parseInt(trimValue_0.charAt(i + 2), 10));
      }
      return sum % 10 === 0 || t("checkout:form.ach.routingInvalid");
    };
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t14) {
    t15 = {
      validRoutingPrefix: t12,
      [validationKeys.differentNumbers]: t13,
      validRoutingChecksum: t14
    };
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== fieldProps || $[40] !== t10 || $[41] !== t11 || $[42] !== t15 || $[43] !== t8 || $[44] !== t9) {
    t16 = <InputField autoComplete="off" label={t8} inputMode="numeric" onFocus={t9} onBlur={t10} onChange={t11} validate={t15} {...fieldProps} />;
    $[39] = fieldProps;
    $[40] = t10;
    $[41] = t11;
    $[42] = t15;
    $[43] = t8;
    $[44] = t9;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};